/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import {
  rollbackRecord,
  saveOrDiscard,
} from '@linc-technologies/linced-common/utils/record-rollback';
import config from 'linced-app/config/environment';
import moment from 'moment';

export default class GoalPicker extends Component {
  // Services
  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // peak time definitions
  nowInterval = moment.duration(1, 'minutes');

  peakInterval = moment.duration(1, 'hours');

  @tracked
  deleteGoalDialogOpen = false;

  @tracked
  goalToDelete = null;

  peakFormat = 'HH:mm';

  peakPrecision = 'minute';

  morningPeakStart = '08:45';

  morningPeakEnd = '09:15';

  @tracked
  selectedCategory = null;

  @action
  setSelectedCategory(value) {
    this.selectedCategory = value;
  }

  @tracked
  categories;

  @tracked
  completeAllInProgress;

  @tracked
  editingGoal;

  @tracked
  goalCategory;

  @tracked
  goalDialogOpen;

  @tracked
  goalSubcategory;

  @action
  setGoalSubcategory(value) {
    this.goalSubcategory = value;
  }

  @tracked
  goalText;

  @action
  setGoalText(value) {
    this.goalText = value;
  }

  @tracked
  subject;

  // Callback to trigger when a PLP goal has been added.
  onAddPlpGoal() {}

  init() {
    super.init(...arguments);

    if (!this.categories) {
      this.categories = this.subject.categories;
    }
  }

  @computed('states.@each.awarded', 'goals.@each.id')
  get progressData() {
    let states = this.states;
    let goals = this.goals;
    let categories = this.categories;
    let progress = [];

    categories.forEach((category) => {
      let data = EmberObject.create({
        categoryId: category.id,
        total: 0,
        developing: 0,
        secure: 0,
      });
      goals
        .filter((goal) => goal.category_id === category.id)
        .forEach((goal) => {
          let state = states.find((state) => state.goal_id === goal.id);
          data.incrementProperty('total');
          if (state) {
            switch (state.get('awarded')) {
              case GoalState.DEVELOPING:
                data.incrementProperty('developing');
                break;
              case GoalState.SECURE:
                data.incrementProperty('secure');
                break;
            }
          }
        });
      let total = data.get('total');
      if (total > 0) {
        data.set('securePercent', (data.get('secure') / total) * 100);
        data.set(
          'developingPercent',
          (data.get('developing') / total) * 100 + data.get('securePercent'),
        );
        if (data.get('secure') === total) {
          category.set('allGoalsCompleted', true);
        } else {
          category.set('allGoalsCompleted', false);
        }
      }
      progress.push(data);
    });

    return progress;
  }

  @computed('states.@each.goal_id')
  get statesByGoal() {
    if (isEmpty(this.states)) {
      return {};
    }
    return this.states.reduce((map, state) => {
      map[state.goal_id] = state;
      return map;
    }, {});
  }

  @action
  setSubject(subject) {
    this.subject = subject;
  }

  @action
  updateState(goal, previousState, grade) {
    let awarded = GoalState.DEVELOPING;

    switch (previousState) {
      case GoalState.DEVELOPING:
        awarded = GoalState.SECURE;
        break;
      case GoalState.SECURE:
        awarded = GoalState.NOT_SET;
        break;
    }

    let stateParams = {
      schoolId: this.get('session.school'),
      assessorId: this.get('session.person'),
      personId: this.person,
      goalId: goal,
      awarded,
    };
    if (!isEmpty(grade)) {
      stateParams.grade = grade;
    }
    let newState = this.store.createRecord('goals/state', stateParams);

    saveOrDiscard(newState)
      .then(() => {
        this.states.update();
      })
      .catch(() => this.toaster.error('Error saving the goal state.'));
  }

  @action
  updateGrade() {
    this.states.update();
  }

  @action
  addGoal(category) {
    this.resetGoal();
    this.goalCategory = category;
    this.goalDialogOpen = true;
  }

  @action
  async editGoal(goal) {
    this.resetGoal();
    this.editingGoal = goal;
    // A belongsTo takes a record, not the async proxy.
    const subcategory = await goal.subcategory;
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.goalSubcategory = subcategory;
    this.goalText = goal.get('educatorDescription');
    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
  }

  @action
  closeDeleteDialog() {
    this.deleteGoalDialogOpen = false;
  }

  @action
  openDeleteDialog(goal) {
    this.deleteGoalDialogOpen = true;
    this.goalToDelete = goal;
  }

  @action
  deleteGoal() {
    this.goalToDelete
      .destroyRecord()
      .then(() => {
        this.toaster.success('Goal deleted successfully!');
      })
      .catch(() => {
        this.toaster.error('Failed to delete goal!');
      });
    this.deleteGoalDialogOpen = false;
  }

  @action
  saveGoal() {
    let goal = this.editingGoal;
    let toaster = this.paperToaster;

    if (isEmpty(goal)) {
      let category = this.goalCategory;
      let subcategory = this.goalSubcategory;
      let sequence =
        (isEmpty(subcategory)
          ? category.get('goals.length')
          : subcategory.get('goals.length')) + 1;
      goal = this.store.createRecord('goals/goal', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.person'),
        isPLP: true,
        personId: this.person,
        categoryId: category,
        sequence,
      });
    }

    goal.setProperties({
      subcategoryId: this.goalSubcategory,
      label: this.goalText,
      educatorDescription: this.goalText,
      peopleDescription: this.goalText,
    });

    goal
      .save()
      .then(() => {
        this.goalDialogOpen = false;
        // this.goals.update();
        toaster.show(`Goal "${goal.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
        this.onAddPlpGoal();
      })
      .catch(() => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(goal);
        toaster.show(`Error saving goal.`, {
          duration: 4000,
          position: 'top right',
        });
      });
  }

  @action
  completeAll() {
    let category = this.selectedCategory;
    let states = this.states;
    let allGoals = [];
    let promises = [];

    // Set flag to say that we're working
    this.completeAllInProgress = true;

    // Grab all non-subcategorised goals
    allGoals.push(...category.get('sortedBoundGoals'));

    // Grab all goals from each subcategory
    let subcats = category.get('sortedBoundSubcategories');
    if (!isEmpty(subcats)) {
      subcats.forEach((subcat) => {
        allGoals.push(...subcat.get('sortedBoundGoals'));
      });
    }

    // Complete any goals which aren't already complete
    let stateBase = {
      schoolId: this.get('session.school'),
      assessorId: this.get('session.person'),
      personId: this.person,
      awarded: GoalState.SECURE,
    };
    allGoals.forEach((goal) => {
      let state = states.find((state) => state.goal_id === goal.id);
      if (isEmpty(state) || state.get('awarded') !== GoalState.SECURE) {
        let newState = this.store.createRecord('goals/state', stateBase);
        newState.set('goalId', goal);
        promises.push(saveOrDiscard(newState));
      }
    });

    // Once all promises have resolved, refresh the local states and clear the in-progress flag.
    Promise.allSettled(promises).then(() => {
      this.states.update();
      this.completeAllInProgress = false;
    });
  }

  resetGoal() {
    this.editingGoal = null;
    this.goalText = '';
    this.goalSubcategory = null;
  }

  @action
  addBadge() {
    let badgeObject = {
      schoolId: this.get('session.school'),
      assessorId: this.get('session.person'),
      personId: this.person,
      subjectId: this.subject,
      categoryId: this.selectedCategory,
      mediaId: this.get('selectedCategory.badgeMediaId'),
      badgeAssertion: {
        assertionId: 'https://app.linc-ed.com/assertions/',
        context: 'https://w3id.org/openbadges/v2',
        type: 'Assertion',
        recipient: {
          type: 'email',
          identity: this.person.get('fields.core:studentEmailAddress.value'),
        },
        issuedOn: moment().format(),
        verification: {
          type: 'hosted',
        },
        badge: {
          badgeId: 'https://app.linc-ed.com/badges/',
          type: 'BadgeClass',
          name: [this.get('subject.label'), this.get('selectedCategory.label')]
            .filter(Boolean)
            .join(' - '),
          description: [
            this.get('subject.description'),
            this.get('selectedCategory.description'),
          ]
            .filter(Boolean)
            .join(' - '),
          image: `${config.APP.apiHost}/media/${
            config.APP.apiPath
          }/media/${this.get('selectedCategory.badgeMedia')}/badge`,
          criteria: {
            narrative: this.get('selectedCategory.badgeDescription'),
          },
          issuer: {
            issuerId:
              'https://app.linc-ed.com/issuer/' + this.get('session.school.id'),
            type: 'Profile',
            name: this.get('session.school.name'),
            url: this.get('session.school.domain'),
            email: this.get('session.school.emailAddress'),
          },
        },
      },
    };

    let badgeRecord = this.store.createRecord('goals/badge', badgeObject);
    badgeRecord
      .save()
      .then((badgeRecord) => {
        this.badges.update();
        this.paperToaster.show(
          `Badge "${badgeRecord.get('badgeAssertion.badge.name')}" saved.`,
          {
            duration: 4000,
            position: 'top right',
          },
        );
      })
      .catch(() => {
        this.paperToaster.show(`Error saving Badge.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }
}
